import type { Schemas } from "@/shared/api/client";
import { cn } from "@/shared/lib/cn";

type TaskStatus = Schemas["TaskStatus"];

const COLOR: Record<TaskStatus, string> = {
  backlog: "text-status-backlog",
  todo: "text-status-todo",
  in_progress: "text-status-in-progress",
  in_review: "text-status-in-review",
  done: "text-status-done",
  cancelled: "text-status-cancelled",
};

/* Share of the circle filled for statuses on the way to done. */
const PROGRESS: Partial<Record<TaskStatus, number>> = {
  in_progress: 0.5,
  in_review: 0.75,
};

const RADIUS = 3.5;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * One glyph per status, distinguishable by shape as well as colour (a dashed ring, an empty
 * ring, a filling pie, a check, a cross), so it never relies on colour alone.
 */
export function StatusIcon({ status, className }: { status: TaskStatus; className?: string }) {
  const progress = PROGRESS[status];
  return (
    <svg
      viewBox="0 0 14 14"
      aria-hidden="true"
      className={cn("size-3.5 shrink-0", COLOR[status], className)}
    >
      {status === "done" || status === "cancelled" ? (
        <>
          <circle cx="7" cy="7" r="6" fill="currentColor" />
          <path
            d={status === "done" ? "M4.3 7.2 6.1 9l3.6-3.8" : "M5 5l4 4M9 5 5 9"}
            fill="none"
            className="stroke-surface"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : (
        <>
          <circle
            cx="7"
            cy="7"
            r="6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray={status === "backlog" ? "1.6 1.9" : undefined}
          />
          {progress !== undefined ? (
            <circle
              cx="7"
              cy="7"
              r={RADIUS / 2}
              fill="none"
              stroke="currentColor"
              strokeWidth={RADIUS}
              strokeDasharray={`${String(CIRCUMFERENCE * progress * 0.5)} ${String(CIRCUMFERENCE)}`}
              transform="rotate(-90 7 7)"
            />
          ) : null}
        </>
      )}
    </svg>
  );
}
